import type { TourRole } from '../../utils/user-roles';
import type { StepView } from './tour-steps';

// The lab/project the deep-feature tour steps deep-link into.
export interface TourLabContext {
  labId: number | null;
  // True when the resolved lab is inactive. Inactive labs hide the sequence
  // upload / CSV metadata controls, so those steps must be dropped to avoid
  // unanchored ("free floating") popovers.
  labInactive: boolean;
  projectLabId: number | null;
  projectId: number | null;
}

interface LabRef {
  id: number;
  inactive?: boolean;
}
interface ProjectRef {
  id: number;
  lab: number;
}

// Prefer an active lab, falling back to the first lab of any status.
function firstActiveOrAny(labs: readonly LabRef[]): LabRef | null {
  return labs.find((l) => !l.inactive) ?? labs[0] ?? null;
}

// Resolve which lab and project the tour should point at for this user.
// Lab-scoped users tour their *selected* lab (an active one if nothing is
// selected); admins and data analysts tour the first active lab. The tour
// prefers active labs because inactive labs hide the upload controls the
// sequence steps anchor to.
export function tourLabContext(
  role: TourRole,
  labs: readonly LabRef[],
  projects: readonly ProjectRef[],
  selectedLabId: number | null
): TourLabContext {
  const chosen =
    role === 'labScoped'
      ? labs.find((l) => l.id === selectedLabId) ?? firstActiveOrAny(labs)
      : firstActiveOrAny(labs);

  const labId = chosen?.id ?? null;
  const project =
    role === 'labScoped' ? projects.find((p) => p.lab === labId) ?? null : projects[0] ?? null;

  return {
    labId,
    labInactive: !!chosen?.inactive,
    projectLabId: project?.lab ?? null,
    projectId: project?.id ?? null,
  };
}

// Parse the lab (and project) id out of the current route. Used by page-scoped
// help so its steps deep-link into the lab/project the user is actually looking
// at, not the tour's default resolved lab.
export function parseRouteLabProject(url: string): { labId: number | null; projectId: number | null } {
  const path = (url || '').split('?')[0];
  const project = path.match(/^\/labs\/(\d+)\/project\/(\d+)/);
  if (project) {
    return { labId: Number(project[1]), projectId: Number(project[2]) };
  }
  const lab = path.match(/^\/labs\/(\d+)/);
  if (lab) {
    return { labId: Number(lab[1]), projectId: null };
  }
  return { labId: null, projectId: null };
}

// Whether a step's target element will actually render for this context. Steps
// are dropped rather than shown, because driver.js floats an unanchored popover
// in the center of the screen when its target is missing:
//   - no lab: the lab detail / sequences / roster pages don't apply
//   - inactive lab: the sequence upload and CSV metadata controls are hidden
//   - no project: the project detail / datasets pages don't apply
export function isStepAvailableForContext(view: StepView, ctx: TourLabContext): boolean {
  if (
    ctx.labId == null &&
    (view === 'lab-detail' || view === 'lab-seq' || view === 'lab-seq-csv' || view === 'lab-roster')
  ) {
    return false;
  }
  if (ctx.labInactive && (view === 'lab-seq' || view === 'lab-seq-csv')) {
    return false;
  }
  if (
    (ctx.projectId == null || ctx.projectLabId == null) &&
    (view === 'project-detail' || view === 'project-detail-datasets')
  ) {
    return false;
  }
  return true;
}
